import { useSyncExternalStore } from 'react'
import { readPreference, writePreference } from './storage'

/**
 * Light and dark themes. "system" follows prefers-color-scheme; "light" and "dark"
 * set <html data-theme>, which tokens.css reads.
 */
export type ThemeChoice = 'system' | 'light' | 'dark'

const listeners = new Set<() => void>()

function isThemeChoice(value: string | null): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark'
}

export function getThemeChoice(): ThemeChoice {
  const stored = readPreference('theme')
  return isThemeChoice(stored) ? stored : 'system'
}

export function applyTheme(choice: ThemeChoice, root: HTMLElement = document.documentElement): void {
  if (choice === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)
}

export function setThemeChoice(choice: ThemeChoice): void {
  writePreference('theme', choice === 'system' ? null : choice)
  applyTheme(choice)
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useThemeChoice(): [ThemeChoice, (choice: ThemeChoice) => void] {
  const choice = useSyncExternalStore(subscribe, getThemeChoice, () => 'system' as const)
  return [choice, setThemeChoice]
}

/**
 * Light-mode colours. "fern" is the default and sets nothing; the others set
 * <html data-palette>, which tokens.css reads. Dark mode ignores the palette.
 */
export type PaletteChoice = 'fern' | 'sky' | 'powder' | 'blueprint'

export const PALETTES: readonly PaletteChoice[] = ['fern', 'sky', 'powder', 'blueprint']

function isPaletteChoice(value: string | null): value is PaletteChoice {
  return PALETTES.includes(value as PaletteChoice)
}

export function getPaletteChoice(): PaletteChoice {
  const stored = readPreference('palette')
  return isPaletteChoice(stored) ? stored : 'fern'
}

export function applyPalette(choice: PaletteChoice, root: HTMLElement = document.documentElement): void {
  if (choice === 'fern') root.removeAttribute('data-palette')
  else root.setAttribute('data-palette', choice)
}

export function setPaletteChoice(choice: PaletteChoice): void {
  writePreference('palette', choice === 'fern' ? null : choice)
  applyPalette(choice)
  listeners.forEach((listener) => listener())
}

export function usePaletteChoice(): [PaletteChoice, (choice: PaletteChoice) => void] {
  const choice = useSyncExternalStore(subscribe, getPaletteChoice, () => 'fern' as const)
  return [choice, setPaletteChoice]
}
